import { Trophy, Star, TrendingUp, Award, Share2, ArrowLeft, Bell, Shield } from "lucide-react";

import { ActiveSessionsManager } from "@/components/dashboard/ActiveSessionsManager";
import { AchievementBadge } from "@/components/gamification/AchievementBadge";
import { ChatPreferences } from "@/components/profile/ChatPreferences";
import { CommunicationCodeManagement } from "@/components/profile/CommunicationCodeManagement";
import { EmailPreferences } from "@/components/profile/EmailPreferences";
import { ProfileSettings } from "@/components/profile/ProfileSettings";
import { PasskeyManager } from "@/components/security/PasskeyManager";
import { PasskeySetup } from "@/components/security/PasskeySetup";
import { TrustedDevicesManager } from "@/components/security/TrustedDevicesManager";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonWithShortcut } from "@/components/ui/button-with-shortcut";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useProfileState } from "@/hooks/useProfileState";

const Profile = () => {
  // Use centralized profile state management
  const {
    user,
    gamificationData,
    achievements,
    topAchievements,
    loading,
    activeTab,
    handleShareProfile,
    handleViewAchievements,
    handleViewNotifications,
    handleGoBack,
    handleTabChange,
  } = useProfileState();

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <ButtonWithShortcut 
          variant="ghost" 
          size="icon" 
          onClick={handleGoBack}
          shortcut="Esc"
          title="Go back (Esc)"
        >
          <ArrowLeft className="h-5 w-5" />
        </ButtonWithShortcut>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Your Profile</h1>
          <p className="text-muted-foreground">Manage your profile and preferences</p>
        </div>
        <Button variant="outline" onClick={handleShareProfile}>
          <Share2 className="h-4 w-4 mr-2" />
          Share Profile
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="security">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Profile Stats Card */}
          <Card className="bg-linear-to-br from-primary/10 via-primary/5 to-background">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-linear-to-br from-primary to-primary/60 flex items-center justify-center text-4xl">
                    👤
                  </div>
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      {user?.email?.split("@")[0] || "Player"}
                      <Badge variant="secondary" className="text-lg">
                        <Star className="h-4 w-4 mr-1" />
                        Level {gamificationData?.current_level || 0}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-base mt-1">
                      {gamificationData?.total_points.toLocaleString() || 0} total points
                    </CardDescription>
                  </div>
                </div>
                <TrendingUp className="h-12 w-12 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center p-3 rounded-lg bg-background/50">
                  <Trophy className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{achievements.length}</p>
                  <p className="text-xs text-muted-foreground">Achievements</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background/50">
                  <p className="text-2xl font-bold">{gamificationData?.boards_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Boards</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background/50">
                  <p className="text-2xl font-bold">{gamificationData?.projects_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Projects</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background/50">
                  <p className="text-2xl font-bold">{gamificationData?.sessions_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Sessions</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background/50">
                  <p className="text-2xl font-bold">{gamificationData?.messages_sent || 0}</p>
                  <p className="text-xs text-muted-foreground">Messages</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-background/50">
                  <p className="text-2xl font-bold">{gamificationData?.memories_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Memories</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Achievement Showcase */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                Achievement Showcase
              </CardTitle>
              <CardDescription>
                Your top achievements - these are displayed on your profile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {topAchievements.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 justify-items-center">
                  {topAchievements.map((achievement) => (
                    <AchievementBadge
                      key={achievement.id}
                      achievement={achievement}
                      size="lg"
                      showShareButton={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No achievements unlocked yet</p>
                  <p className="text-sm">Start completing activities to earn your first achievement!</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* All Achievements */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-6 w-6" />
                    All Achievements ({achievements.length})
                  </CardTitle>
                  <CardDescription>All the achievements you've unlocked</CardDescription>
                </div>
                <Button variant="outline" onClick={handleViewAchievements}>
                  View Progress
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {achievements.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 justify-items-center">
                  {achievements.map((achievement) => (
                    <AchievementBadge
                      key={achievement.id}
                      achievement={achievement}
                      size="md"
                      showShareButton={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No achievements yet - get started on your journey!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <CommunicationCodeManagement userId={user?.id || ''} />
          
          <Card>
            <CardHeader>
              <CardTitle>Passkey Authentication</CardTitle>
              <CardDescription>
                Register passkeys for passwordless authentication using your device's biometrics or security key
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <PasskeySetup />
              <Separator />
              <PasskeyManager />
            </CardContent>
          </Card>
          
          <ActiveSessionsManager />
          
          <TrustedDevicesManager />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <ProfileSettings />
          
          <EmailPreferences />
          
          <ChatPreferences />
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification History
              </CardTitle>
              <CardDescription>
                View all emails sent to your account and see how your preferences affect them
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleViewNotifications} className="w-full sm:w-auto">
                View Notification History
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
